import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useUserContext } from '../../contexts/UserContext';
import {
    Btn,
    IconPlus,
    IconTrend,
    PageHeader,
    TEIQspinner,
} from '../ui';
import { CustomModal } from '../CustomModal';
import {
    clearTodaysTopic,
    createBriefingTopic,
    findTodaysTopic,
    getConfirmationStats,
    listBriefingTopics,
    markAsSafetyLesson,
    setTodaysTopic,
    unmarkSafetyLesson,
    updateTopicTitle,
    applyScheduleDraft,
    ScheduleDraftApplyError,
    type BriefingTopic,
    type ConfirmationRow,
    type ScheduleDraftEntry,
} from './dailyBriefingData';
import { TodaysBriefingCard } from './TodaysBriefingCard';
import { TopicLibraryCard } from './TopicLibraryCard';
import { UpgradeUpsellCard } from './UpgradeUpsellCard';
import { UpgradeTrainingModal } from './UpgradeTrainingModal';
import { LaunchDisplayModal } from './LaunchDisplayModal';
import { ChangeTopicModal } from './ChangeTopicModal';
import { ConfirmationsModal } from './ConfirmationsModal';
import { AddTopicModal } from './AddTopicModal';
import { ImportFromResourcesModal } from './ImportFromResourcesModal';
import { EditTopicModal } from './EditTopicModal';
import { ScheduleTopicsModal } from './ScheduleTopicsModal';
import '../../css/DailyBriefing.css';

export function DailyBriefingPage() {
    const { company, companyOwner, allCompanyUsers } = useUserContext();
    const companyId = company?.id ?? '';
    const hasTrainingModule = Boolean(company?.trainingModuleEnrollment);

    const [loading, setLoading] = useState(true);
    const [topics, setTopics] = useState<BriefingTopic[]>([]);
    const [todayTopic, setTodayTopic] = useState<BriefingTopic | null>(null);
    const [confirmedCount, setConfirmedCount] = useState(0);
    const [crewCount, setCrewCount] = useState(0);
    const [confirmationRows, setConfirmationRows] = useState<ConfirmationRow[]>([]);
    const [error, setError] = useState<string | null>(null);

    const [launchOpen, setLaunchOpen] = useState(false);
    const [launchTopic, setLaunchTopic] = useState<BriefingTopic | null>(null);
    const [changeOpen, setChangeOpen] = useState(false);
    const [confirmationsOpen, setConfirmationsOpen] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [editTopic, setEditTopic] = useState<BriefingTopic | null>(null);
    const [deleteTopic, setDeleteTopic] = useState<BriefingTopic | null>(null);
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const refresh = useCallback(async () => {
        if (!companyId) return;
        setError(null);
        try {
            const nextTopics = await listBriefingTopics(companyId);
            const today = findTodaysTopic(nextTopics, companyId);
            const stats = getConfirmationStats(allCompanyUsers ?? [], today?.id ?? null);
            setTopics(nextTopics);
            setTodayTopic(today);
            setConfirmedCount(stats.confirmedCount);
            setCrewCount(stats.crewCount);
            setConfirmationRows(stats.rows);
        } catch (err) {
            console.error('Failed to load daily briefing data', err);
            Sentry.captureException(err);
            setError('Unable to load Daily Briefing. Please try again.');
        }
    }, [allCompanyUsers, companyId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!companyId) {
                setLoading(false);
                return;
            }
            setLoading(true);
            await refresh();
            if (!cancelled) setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [companyId, refresh]);

    const openLaunch = (topic: BriefingTopic | null) => {
        if (!topic) return;
        setLaunchTopic(topic);
        setLaunchOpen(true);
    };

    const handleSetToday = async (topic: BriefingTopic) => {
        if (!companyId) return;
        setSaving(true);
        try {
            await setTodaysTopic({
                companyId,
                nextTopic: topic,
                allTopics: topics,
            });
            await refresh();
            setChangeOpen(false);
        } catch (err) {
            console.error('Failed to set today’s topic', err);
            Sentry.captureException(err);
            const detail = err instanceof Error && err.message ? ` ${err.message}` : '';
            setError(`Could not change today’s topic.${detail}`);
        } finally {
            setSaving(false);
        }
    };

    const handleClearToday = async () => {
        if (!companyId || !todayTopic) return;
        setSaving(true);
        setError(null);
        try {
            await clearTodaysTopic({
                companyId,
                topic: todayTopic,
                allTopics: topics,
            });
            await refresh();
        } catch (err) {
            console.error('Failed to clear today’s topic', err);
            Sentry.captureException(err);
            const detail = err instanceof Error && err.message ? ` ${err.message}` : '';
            setError(`Could not clear today’s topic.${detail}`);
        } finally {
            setSaving(false);
        }
    };

    const handleScheduleSave = async (payload: {
        draft: Record<string, ScheduleDraftEntry>;
        baseline: Record<string, ScheduleDraftEntry>;
    }) => {
        if (!companyId) return;
        setSaving(true);
        setError(null);
        try {
            await applyScheduleDraft({
                companyId,
                draft: payload.draft,
                baseline: payload.baseline,
                allTopics: topics,
            });
            await refresh();
            setScheduleOpen(false);
        } catch (err) {
            console.error('Failed to save schedule', err);
            Sentry.captureException(err);
            // Reload so the UI reflects any topics that did save before a failure.
            try {
                await refresh();
            } catch {
                /* ignore refresh errors after save failure */
            }
            if (err instanceof ScheduleDraftApplyError) {
                const detail = err.failures
                    .map((f) => `“${f.title || f.topicId}” (${f.dateKeys.join(', ')})`)
                    .join('; ');
                setError(`Could not save schedule. ${err.message} ${detail}`);
            } else {
                const detail = err instanceof Error && err.message ? err.message : 'Please try again.';
                setError(`Could not save schedule. ${detail}`);
            }
            throw err;
        } finally {
            setSaving(false);
        }
    };

    const handleAddTopic = async ({ title, file }: { title: string; file: File }) => {
        if (!companyId) return;
        const ownerId = companyOwner?.id || companyId;
        setSaving(true);
        setError(null);
        try {
            const created = await createBriefingTopic({ companyId, ownerId, title, file });
            await refresh();
            // Ensure the new topic is visible even if list lag/filter misses it once.
            setTopics((prev) => {
                if (prev.some((t) => t.id === created.id)) return prev;
                return [...prev, created].sort((a, b) =>
                    a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }),
                );
            });
            setAddOpen(false);
        } catch (err) {
            console.error('Failed to add topic', err);
            Sentry.captureException(err);
            const detail = err instanceof Error && err.message ? ` ${err.message}` : '';
            setError(`Could not add topic.${detail}`);
        } finally {
            setSaving(false);
        }
    };

    const handleImport = async (selected: BriefingTopic[]) => {
        setSaving(true);
        try {
            for (const topic of selected) {
                await markAsSafetyLesson(topic);
            }
            await refresh();
            setImportOpen(false);
        } catch (err) {
            console.error('Failed to import topics', err);
            Sentry.captureException(err);
            setError('Could not import topics. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleEditSave = async (title: string) => {
        if (!editTopic) return;
        setSaving(true);
        try {
            await updateTopicTitle(editTopic, title);
            await refresh();
            setEditTopic(null);
        } catch (err) {
            console.error('Failed to edit topic', err);
            Sentry.captureException(err);
            setError('Could not update topic title.');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTopic) return;
        setSaving(true);
        try {
            await unmarkSafetyLesson(deleteTopic);
            await refresh();
            setDeleteTopic(null);
        } catch (err) {
            console.error('Failed to remove topic', err);
            Sentry.captureException(err);
            setError('Could not remove topic from the library.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="page-container daily-briefing">
                <div className="db-page-loading">
                    <TEIQspinner />
                </div>
            </div>
        );
    }

    return (
        <div className="page-container daily-briefing">
            <PageHeader
                title="Daily Briefing"
                subtitle="Upload topics for your crew to view each morning. Users confirm they’ve seen them when they log in."
                actions={
                    <>
                        {!hasTrainingModule && (
                            <Btn
                                variant="warning"
                                buttonText="Upgrade to Full Safety & Training"
                                buttonIcon={<IconTrend />}
                                buttonOnClick={() => setUpgradeOpen(true)}
                            />
                        )}
                        <Btn
                            variant="primary"
                            buttonText="Add Topic"
                            buttonIcon={<IconPlus />}
                            buttonOnClick={() => setAddOpen(true)}
                        />
                    </>
                }
                className="db-page-header"
            />

            {error && (
                <p className="db-page-error" role="alert">
                    {error}
                </p>
            )}

            <TodaysBriefingCard
                topic={todayTopic}
                confirmedCount={confirmedCount}
                crewCount={crewCount}
                clearing={saving}
                onLaunch={() => openLaunch(todayTopic)}
                onChangeTopic={() => setChangeOpen(true)}
                onScheduleTopics={() => setScheduleOpen(true)}
                onClearTopic={handleClearToday}
                onViewConfirmations={() => setConfirmationsOpen(true)}
            />

            <TopicLibraryCard
                topics={topics}
                onImport={() => setImportOpen(true)}
                onPreview={(topic) => openLaunch(topic)}
                onEdit={(topic) => setEditTopic(topic)}
                onDelete={async (topic) => setDeleteTopic(topic)}
            />

            {!hasTrainingModule && (
                <UpgradeUpsellCard onUpgrade={() => setUpgradeOpen(true)} />
            )}

            <UpgradeTrainingModal
                visible={upgradeOpen}
                topicCount={topics.length}
                onDismiss={() => setUpgradeOpen(false)}
            />

            <ScheduleTopicsModal
                visible={scheduleOpen}
                companyId={companyId}
                topics={topics}
                saving={saving}
                onDismiss={() => setScheduleOpen(false)}
                onSave={handleScheduleSave}
            />

            <LaunchDisplayModal
                visible={launchOpen}
                topic={launchTopic}
                onDismiss={() => {
                    setLaunchOpen(false);
                    setLaunchTopic(null);
                }}
                onMarkedAsViewed={(updatedUsers) => {
                    const topicId = launchTopic?.id ?? todayTopic?.id ?? null;
                    const stats = getConfirmationStats(updatedUsers, topicId);
                    setConfirmedCount(stats.confirmedCount);
                    setCrewCount(stats.crewCount);
                    setConfirmationRows(stats.rows);
                }}
            />
            <ChangeTopicModal
                visible={changeOpen}
                topics={topics}
                currentTopic={todayTopic}
                currentTopicId={todayTopic?.id ?? null}
                confirmedCount={confirmedCount}
                crewCount={crewCount}
                saving={saving}
                onDismiss={() => setChangeOpen(false)}
                onSelect={handleSetToday}
            />
            <ConfirmationsModal
                visible={confirmationsOpen}
                rows={confirmationRows}
                confirmedCount={confirmedCount}
                crewCount={crewCount}
                topicTitle={todayTopic?.title ?? null}
                onDismiss={() => setConfirmationsOpen(false)}
            />
            <AddTopicModal
                visible={addOpen}
                saving={saving}
                onDismiss={() => setAddOpen(false)}
                onSave={handleAddTopic}
                onUpgrade={() => setUpgradeOpen(true)}
            />
            <ImportFromResourcesModal
                visible={importOpen}
                companyId={companyId}
                saving={saving}
                onDismiss={() => setImportOpen(false)}
                onImport={handleImport}
            />
            <EditTopicModal
                visible={Boolean(editTopic)}
                topic={editTopic}
                saving={saving}
                onDismiss={() => setEditTopic(null)}
                onSave={handleEditSave}
            />
            <CustomModal
                visible={Boolean(deleteTopic)}
                onDismiss={() => setDeleteTopic(null)}
                header="Remove topic"
                footer={
                    <div className="db-modal-footer-actions">
                        <Btn
                            variant="secondary"
                            buttonText="Cancel"
                            buttonOnClick={() => setDeleteTopic(null)}
                            buttonDisabled={saving}
                        />
                        <Btn
                            variant="danger"
                            buttonText={saving ? 'Removing…' : 'Remove'}
                            buttonOnClick={handleDeleteConfirm}
                            buttonDisabled={saving}
                        />
                    </div>
                }
            >
                <p>
                    Remove “{deleteTopic?.title}” from the Daily Briefing topic library? The file will
                    remain in Resources.
                </p>
            </CustomModal>
        </div>
    );
}
